import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { vehicleCreateAPISchema } from '@hero/api';
import type { Vehicle } from '@hero/schema';

export type VehicleFormValues = z.infer<typeof vehicleCreateAPISchema>;

export interface UseVehicleFormOptions {
  defaultValues?: Partial<VehicleFormValues>;
  onSubmit?: (values: VehicleFormValues) => void | Promise<void>;
}

export type UseVehicleFormHook = ReturnType<typeof useVehicleForm>;

export function useVehicleForm(options: UseVehicleFormOptions = {}) {
  const { defaultValues: defaultValuesProp, onSubmit } = options;

  function vehicleToFormValues(vehicle: Vehicle): Partial<VehicleFormValues> {
    // Every classification/registration field except `type` and
    // `plate` is optional now (recommended for Logisti, not required
    // for creation). Use optional-chains so a freshly-created vehicle
    // that only has type+plate can still seed the edit form.
    const regImageUrl = vehicle.registration.image?.url;
    return {
      vehicle: vehicle.classification.type,
      plate: vehicle.registration.plate,
      plateType: vehicle.registration.plateType,
      brand: vehicle.classification.brand,
      year: vehicle.classification.year,
      color: vehicle.classification.color,
      country: vehicle.registration.country,
      driverId: vehicle.driver?.driverId,
      registrationImage: regImageUrl ? { upload: regImageUrl } : undefined,
      vehicleImages: vehicle.images?.map((img: any) => ({ upload: img.url })),
    };
  }

  const form = useForm<VehicleFormValues>({
    resolver: zodResolver(vehicleCreateAPISchema),
    defaultValues: defaultValuesProp,
  });

  const values = form.watch();

  const reset = useCallback(
    (vals?: Partial<VehicleFormValues>) => {
      form.reset(vals);
    },
    [form],
  );

  const onValidSubmit = useCallback(
    (vals: VehicleFormValues) => {
      onSubmit?.(vals);
    },
    [onSubmit],
  );

  const isFormValid = useMemo(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const setVehicle = useCallback(
    (vehicle: Vehicle) => {
      const formValues = vehicleToFormValues(vehicle);
      form.reset(formValues);
    },
    [form],
  );

  return {
    form,
    values,
    isFormValid,
    reset,
    onValidSubmit,
    setValue: form.setValue,
    setVehicle,
  };
}
